import React, { useMemo } from "react";
import moment from "moment";

import { DateFormats } from "@enums/shared.enum";
import { ExceptionsPropType } from "@models/components/organisation.model";
import { applyLocalization } from "@services/common.service";

const Exceptions: React.FC<ExceptionsPropType> = ({ exceptions }) => {
  const groupedExceptions = useMemo(() => {
    if (!exceptions?.dates?.length) return {};

    const expandedList = exceptions.dates.flatMap(item => {
      const start = moment(item.startDate);
      const end = moment(item.endDate);
      const days = end.diff(start, "days");
      return Array.from({ length: days + 1 }, (_, i) => ({
        ...item,
        displayDate: start.clone().add(i, "days").format("YYYY-MM-DD"),
      }));
    });

    return expandedList.reduce<Record<string, typeof expandedList>>(
      (acc, item) => {
        acc[item.displayDate] = acc[item.displayDate] || [];
        acc[item.displayDate].push(item);
        return acc;
      },
      {}
    );
  }, [exceptions]);

  const sortedDates = useMemo(
    () =>
      Object.keys(groupedExceptions).sort(
        (a, b) => moment(a).valueOf() - moment(b).valueOf()
      ),
    [groupedExceptions]
  );

  if (!sortedDates.length) return null;

  return (
    <div className="fg-booking-exception-list-wrapper">
      {sortedDates.map(date => {
        const formattedDate = moment(date).format(DateFormats.YMD);
        return (
          <div className="fg-booking-exception-list" key={date}>
            <h4 className="fg-booking-exception-date mb-0">{formattedDate}</h4>

            <div className="fg-booking-exception-events">
              {groupedExceptions[date].map((item, index) => {
                const formattedStartTime = moment(
                  item.startTime,
                  DateFormats.TimeWithSeconds
                ).format(DateFormats.Time);
                const formattedEndTime = moment(
                  item.endTime,
                  DateFormats.TimeWithSeconds
                ).format(DateFormats.Time);

                return (
                  <div className="fg-booking-exception-event" key={index}>
                    <span className="fg-booking-exception-event-name">
                      {applyLocalization(
                        exceptions?.titles?.[item.exceptionId]?.title
                      )}
                    </span>
                    <span className="fg-booking-exception-event-time">
                      {`${formattedStartTime}–${formattedEndTime}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Exceptions;
